import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Checkbox } from './ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogBody } from './ui/dialog';
import { Loader2, Users } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { who_lives_in_household: '', household_income_source: '', has_criminal_background: false, criminal_background_details: '' };

// Same questions as the case note editor's {{enrollment_summary}} pop-up, used before running a quick note
const EnrollmentSummaryQuestionsDialog = ({ open, caseId, participantId, onComplete, onCancel }) => {
  const [data, setData] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (open) setData(EMPTY); }, [open]);

  const set = (k, v) => setData(prev => ({ ...prev, [k]: v }));
  const valid = data.who_lives_in_household.trim() && data.household_income_source.trim() &&
    (!data.has_criminal_background || data.criminal_background_details.trim());

  const submit = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/case-notes/generate-enrollment-summary`, { case_id: caseId, participant_id: participantId, ...data });
      if (res.data.success && res.data.enrollment_summary) {
        onComplete(res.data.enrollment_summary);
      } else {
        toast.error('Failed to generate enrollment summary');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate enrollment summary');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !loading && onCancel()}>
      <DialogContent className="sm:max-w-lg" data-testid="quick-note-enrollment-summary-popup">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-blue-600" />Enrollment Summary Information</DialogTitle>
          <DialogDescription>This quick note includes an AI-generated enrollment summary. Please answer these questions first.</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-2">
            <Label>Who lives in the participant's household? *</Label>
            <Textarea rows={2} value={data.who_lives_in_household} onChange={(e) => set('who_lives_in_household', e.target.value)}
              placeholder="e.g., Participant lives with spouse and two children (ages 5 and 8)..." data-testid="qn-household-input" />
          </div>
          <div className="space-y-2">
            <Label>What is the household's source of income? *</Label>
            <Textarea rows={2} value={data.household_income_source} onChange={(e) => set('household_income_source', e.target.value)}
              placeholder="e.g., SNAP benefits, part-time employment..." data-testid="qn-income-source-input" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={data.has_criminal_background} data-testid="qn-criminal-background-checkbox"
              onCheckedChange={(c) => setData(prev => ({ ...prev, has_criminal_background: c, criminal_background_details: c ? prev.criminal_background_details : '' }))} />
            Participant has disclosed a criminal background
          </label>
          {data.has_criminal_background && (
            <div className="space-y-2 ml-6">
              <Label>Please provide relevant details *</Label>
              <Textarea rows={2} value={data.criminal_background_details} onChange={(e) => set('criminal_background_details', e.target.value)} data-testid="qn-criminal-details-input" />
            </div>
          )}
        </DialogBody>
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => onComplete(null)} disabled={loading} data-testid="qn-enrollment-summary-skip-btn">Skip for Now</Button>
          <Button onClick={submit} disabled={loading || !valid} data-testid="qn-enrollment-summary-submit-btn">
            {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}Generate Summary &amp; Create Notes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EnrollmentSummaryQuestionsDialog;
